// Standardized error type for all API operations
import * as Sentry from "@sentry/react";

export type ApiErrorCode =
  | 'VALIDATION_ERROR'
  | 'AUTH_INVALID_CREDENTIALS'
  | 'AUTH_USER_EXISTS'
  | 'AUTH_WEAK_PASSWORD'
  | 'AUTH_EMAIL_NOT_CONFIRMED'
  | 'PERMISSION_DENIED'
  | 'RESOURCE_NOT_FOUND'
  | 'RESOURCE_CONFLICT'
  | 'FILE_TOO_LARGE'
  | 'INVALID_FILE_TYPE'
  | 'NETWORK_ERROR'
  | 'TIMEOUT_ERROR'
  | 'SERVER_ERROR'
  | 'UNKNOWN_ERROR'
  | 'RLS_VIOLATION';

// Internal interface for API errors (not exported to avoid conflict with legacy class)
interface IApiError extends Error {
  code: ApiErrorCode;
  message: string;
  status?: number;
  statusCode?: number;
  details?: Record<string, any>;
  isTransient: boolean;
  originalError?: Error;
}

export class ApiErrorImpl extends Error implements IApiError {
  code: ApiErrorCode;
  statusCode?: number;
  details?: Record<string, any>;
  isTransient: boolean;
  originalError?: Error;

  constructor(
    code: ApiErrorCode,
    message: string,
    statusCode?: number,
    isTransient: boolean = false,
    originalError?: Error
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.statusCode = statusCode;
    this.isTransient = isTransient;
    this.originalError = originalError;

    // Maintain proper prototype chain
    Object.setPrototypeOf(this, ApiErrorImpl.prototype);
  }
}

/**
 * Maps Supabase auth errors to standardized ApiError
 */
export function mapAuthError(error: any): IApiError {
  if (!error) {
    return new ApiErrorImpl(
      'UNKNOWN_ERROR',
      'An unknown error occurred',
      undefined,
      true
    );
  }

  // Handle string error messages
  if (typeof error === 'string') {
    if (error.includes('Invalid login credentials')) {
      return new ApiErrorImpl(
        'AUTH_INVALID_CREDENTIALS',
        'Invalid email or password',
        401,
        false,
        error as any
      );
    }
    if (error.includes('User already registered')) {
      return new ApiErrorImpl(
        'AUTH_USER_EXISTS',
        'This email is already registered',
        409,
        false,
        error as any
      );
    }
    if (error.includes('Password')) {
      return new ApiErrorImpl(
        'AUTH_WEAK_PASSWORD',
        'Password must be at least 8 characters',
        400,
        false,
        error as any
      );
    }
  }

  // Handle Supabase error objects
  const errorMsg = error.message || error.msg || JSON.stringify(error);

  if (error.status === 422 || error.status === 400) {
    if (errorMsg.includes('credentials')) {
      return new ApiErrorImpl(
        'AUTH_INVALID_CREDENTIALS',
        'Invalid email or password',
        401,
        false,
        error
      );
    }
    if (errorMsg.includes('already')) {
      return new ApiErrorImpl(
        'AUTH_USER_EXISTS',
        'This email is already registered',
        409,
        false,
        error
      );
    }
    return new ApiErrorImpl(
      'VALIDATION_ERROR',
      errorMsg,
      400,
      false,
      error
    );
  }

  if (error.status === 401 || error.status === 403) {
    return new ApiErrorImpl(
      'AUTH_INVALID_CREDENTIALS',
      'Invalid credentials or permission denied',
      error.status,
      false,
      error
    );
  }

  if (error.status === 404) {
    return new ApiErrorImpl(
      'RESOURCE_NOT_FOUND',
      'Resource not found',
      404,
      false,
      error
    );
  }

  if (error.status === 409) {
    return new ApiErrorImpl(
      'RESOURCE_CONFLICT',
      'Resource conflict (duplicate entry?)',
      409,
      false,
      error
    );
  }

  // Server errors are transient
  if (error.status >= 500) {
    return new ApiErrorImpl(
      'SERVER_ERROR',
      'Server error - will retry automatically',
      error.status,
      true,
      error
    );
  }

  return new ApiErrorImpl(
    'UNKNOWN_ERROR',
    errorMsg || 'An unknown error occurred',
    error.status,
    false,
    error
  );
}

/**
 * Maps database errors to standardized ApiError
 */
export function mapDatabaseError(error: any): IApiError {
  if (!error) {
    return new ApiErrorImpl(
      'UNKNOWN_ERROR',
      'An unknown error occurred',
      undefined,
      true
    );
  }

  const errorMsg = error.message || JSON.stringify(error);

  // RLS violations
  if (error.code === '42501' || errorMsg.includes('row-level security')) {
    return new ApiErrorImpl(
      'RLS_VIOLATION',
      'Permission denied - insufficient access',
      403,
      false,
      error
    );
  }

  // Unique constraint violations
  if (error.code === '23505' || errorMsg.includes('unique')) {
    return new ApiErrorImpl(
      'RESOURCE_CONFLICT',
      'This record already exists',
      409,
      false,
      error
    );
  }

  // Foreign key violations
  if (error.code === '23503' || errorMsg.includes('foreign key')) {
    return new ApiErrorImpl(
      'VALIDATION_ERROR',
      'Referenced record not found',
      400,
      false,
      error
    );
  }

  // Generic validation errors
  if (error.code === '23502' || errorMsg.includes('null')) {
    return new ApiErrorImpl(
      'VALIDATION_ERROR',
      'Required field missing',
      400,
      false,
      error
    );
  }

  return new ApiErrorImpl(
    'UNKNOWN_ERROR',
    errorMsg,
    error.status,
    false,
    error
  );
}

/**
 * Maps storage/file errors to standardized ApiError
 */
export function mapStorageError(error: any): IApiError {
  if (!error) {
    return new ApiErrorImpl(
      'UNKNOWN_ERROR',
      'An unknown error occurred',
      undefined,
      true
    );
  }

  const errorMsg = error.message || JSON.stringify(error);
  const status = error.statusCode || error.status;

  if (status === 413 || errorMsg.includes('size') || errorMsg.includes('large')) {
    return new ApiErrorImpl(
      'FILE_TOO_LARGE',
      'File size exceeds maximum limit (5MB)',
      413,
      false,
      error
    );
  }

  if (status === 415 || errorMsg.includes('type') || errorMsg.includes('MIME')) {
    return new ApiErrorImpl(
      'INVALID_FILE_TYPE',
      'Invalid file type. Only JPG, PNG, WebP allowed',
      415,
      false,
      error
    );
  }

  if (status >= 500) {
    return new ApiErrorImpl(
      'SERVER_ERROR',
      'Server error - will retry automatically',
      status,
      true,
      error
    );
  }

  return new ApiErrorImpl(
    'UNKNOWN_ERROR',
    errorMsg,
    status,
    false,
    error
  );
}

/**
 * Maps network errors to standardized ApiError
 */
export function mapNetworkError(error: any): IApiError {
  if (!error) {
    return new ApiErrorImpl(
      'UNKNOWN_ERROR',
      'An unknown error occurred',
      undefined,
      true
    );
  }

  const errorMsg = String(error.message || error);

  if (errorMsg.includes('timeout') || errorMsg.includes('Timeout')) {
    return new ApiErrorImpl(
      'TIMEOUT_ERROR',
      'Request timeout - will retry automatically',
      undefined,
      true,
      error
    );
  }

  if (
    errorMsg.includes('Failed to fetch') ||
    errorMsg.includes('fetch failed') ||
    errorMsg.includes('NetworkError') ||
    errorMsg.includes('network')
  ) {
    return new ApiErrorImpl(
      'NETWORK_ERROR',
      'Network error - will retry automatically',
      undefined,
      true,
      error
    );
  }

  return new ApiErrorImpl(
    'UNKNOWN_ERROR',
    errorMsg,
    undefined,
    true,
    error
  );
}

/**
 * Universal error mapper - routes to specific mappers
 */
export function mapError(error: any, context?: 'auth' | 'database' | 'storage' | 'network'): IApiError {
  if (error instanceof ApiErrorImpl) {
    return error;
  }

  try {
    // Try to detect error type automatically
    if (context === 'auth') {
      return mapAuthError(error);
    }
    if (context === 'storage') {
      return mapStorageError(error);
    }
    if (context === 'database') {
      return mapDatabaseError(error);
    }
    if (context === 'network') {
      return mapNetworkError(error);
    }

    // Auto-detect based on error properties
    if (error.message && error.message.includes('fetch')) {
      return mapNetworkError(error);
    }
    if (error.code === '42501' || error.message?.includes('row-level security')) {
      return mapDatabaseError(error);
    }
    if (error.statusCode === 413 || error.message?.includes('size')) {
      return mapStorageError(error);
    }

    // Default to database error mapping (most common)
    return mapDatabaseError(error);
  } catch (mappingError) {
    Sentry.captureException(mappingError, {
      tags: { context: 'api_error', operation: 'error_mapping' },
      extra: { message: 'Error mapping failed', originalError: error }
    });
    return new ApiErrorImpl(
      'UNKNOWN_ERROR',
      'An unexpected error occurred',
      undefined,
      true,
      error
    );
  }
}

/**
 * Determine if error is transient (should retry) vs permanent
 */
export function isTransientError(error: IApiError | Error | any): boolean {
  if (error instanceof ApiErrorImpl) {
    return error.isTransient;
  }

  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('network') ||
      msg.includes('timeout') ||
      msg.includes('fetch failed')
    );
  }

  // Default: assume transient for safety
  return true;
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: IApiError | Error | any): string {
  if (error instanceof ApiErrorImpl) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred';
}

/**
 * Check if error is a specific type
 */
export function isErrorType(error: IApiError | Error | any, code: ApiErrorCode): boolean {
  if (error instanceof ApiErrorImpl) {
    return error.code === code;
  }
  return false;
}

// Legacy error classes for backward compatibility with old api-client.ts imports
// These are separate from the ApiErrorImpl system above
class ApiErrorClass extends Error {
  isTransient: boolean = false;
  status?: number;
  
  constructor(
    message: string,
    status?: number,
    public code?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

class NetworkErrorClass extends Error {
  isTransient: boolean = true;
  status?: number;
  
  constructor(message: string = 'Network error occurred') {
    super(message);
    this.name = 'NetworkError';
  }
}

class ValidationErrorClass extends Error {
  isTransient: boolean = false;
  status?: number;
  
  constructor(
    message: string,
    public field?: string,
    public errors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ValidationError';
  }
}

class AuthenticationErrorClass extends Error {
  isTransient: boolean = false;
  status?: number;
  
  constructor(message: string = 'Authentication required') {
    super(message);
    this.name = 'AuthenticationError';
  }
}

class AuthorizationErrorClass extends Error {
  isTransient: boolean = false;
  status?: number;
  
  constructor(message: string = 'Access denied') {
    super(message);
    this.name = 'AuthorizationError';
  }
}

// Export with original names for backward compatibility
export {
  ApiErrorClass as ApiError,
  NetworkErrorClass as NetworkError,
  ValidationErrorClass as ValidationError,
  AuthenticationErrorClass as AuthenticationError,
  AuthorizationErrorClass as AuthorizationError
};
